import { Image } from "antd";

type Item = { src: string; alt: string; caption?: string };

type Props = {
    items: Item[];
    /** Optional aria-label for the carousel region. */
    label?: string;
    /** "carousel" (default) — horizontal scroll-snap, full-width photos.
     *  "grid" — compact 110px-tall thumbnails, 1-2 up, used inside a
     *  guide's day-rail right column. */
    variant?: "carousel" | "grid";
};

// Ride photos for a single day. In "carousel" mode this is a horizontal
// scroll-snap strip of full-width shots; in "grid" mode it's a compact
// thumbnail grid sized for a guide day-rail. Both share the antd preview
// group so tapping any image opens full resolution across the set.
export default function GuideDayCarousel({
    items,
    label = "Photos",
    variant = "carousel",
}: Props) {
    const isGrid = variant === "grid";
    return (
        <div
            className={isGrid ? "gt-day-grid" : "gt-day-carousel"}
            role="region"
            aria-label={label}
            style={
                isGrid
                    ? { gridTemplateColumns: items.length > 1 ? "1fr 1fr" : "1fr" }
                    : undefined
            }
        >
            <Image.PreviewGroup>
                {items.map((item) => (
                    <figure
                        key={item.src}
                        className={
                            isGrid ? "gt-day-grid__item" : "gt-day-carousel__item"
                        }
                    >
                        <div
                            className={
                                isGrid
                                    ? "gt-day-grid__frame"
                                    : "gt-day-carousel__frame"
                            }
                        >
                            <Image
                                src={item.src}
                                alt={item.alt}
                                style={
                                    isGrid
                                        ? {
                                              display: "block",
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                          }
                                        : {
                                              display: "block",
                                              width: "100%",
                                              height: "auto",
                                          }
                                }
                            />
                        </div>
                        {item.caption && (
                            <figcaption
                                className={
                                    isGrid
                                        ? "gt-day-grid__caption"
                                        : "gt-day-carousel__caption"
                                }
                            >
                                {item.caption}
                            </figcaption>
                        )}
                    </figure>
                ))}
            </Image.PreviewGroup>
        </div>
    );
}
